import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUser, UserButton } from '@clerk/clerk-react';
import { Logo } from './Logo';
import { Icon } from './icons';
import { ConnectionBadge } from './ConnectionBadge';
import { useTheme } from '../lib/theme';
import { useApiClient } from '../lib/api-client';
import { useRealtime } from '../lib/realtime';
import type { McpApproval } from '../../shared/types';

// Primary check: Clerk user_id (always in JWT — works even without email claim).
// Secondary check: email (requires Clerk session-token email claim to be configured).
const SUPER_ADMIN_USER_IDS = ['user_3EbTPsWXSf4TmWQAwT8rwMqzRHw'];
const SUPER_ADMIN_EMAILS   = ['roblauzon@gmail.com'];

const ADMIN_LINKS = [
  { to: '/admin/eula',      label: 'EULA'      },
  { to: '/admin/feedback',  label: 'Feedback'  },
  { to: '/admin/kofi',      label: 'Ko-Fi'     },
  { to: '/admin/affiliate', label: 'Affiliate' },
  { to: '/admin/users',     label: 'Users'     },
];

/**
 * Global sticky header.
 * Minimal on /sign-in and /eula.
 * Full variant: logo + nav + connection badge + theme + user button.
 *
 * Feedback link always visible to authenticated users.
 * Admin dropdown only visible to super-admins (checked by user_id or email).
 */
export function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { user } = useUser();
  const client = useApiClient();
  const [adminOpen, setAdminOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [pendingAgentRuns, setPendingAgentRuns] = useState(0);
  const adminRef = useRef<HTMLDivElement>(null);

  const minimal =
    pathname === '/sign-in' || pathname.startsWith('/sign-in/') ||
    pathname === '/sign-up'  || pathname.startsWith('/sign-up/')  ||
    pathname === '/eula';

  const isAdmin = user
    ? SUPER_ADMIN_USER_IDS.includes(user.id) ||
      SUPER_ADMIN_EMAILS.includes(
        (user.primaryEmailAddress?.emailAddress ?? '').toLowerCase(),
      )
    : false;

  const isAdminRoute = pathname.startsWith('/admin');

  // Close the admin dropdown when clicking outside.
  useEffect(() => {
    if (!adminOpen) return;
    const handler = (e: MouseEvent) => {
      if (adminRef.current && !adminRef.current.contains(e.target as Node)) {
        setAdminOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [adminOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!adminOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAdminOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [adminOpen]);

  // Poll pending approvals every 60s for the header badge. Cheap — D1 index
  // on (user_id, status, requested_at). Refreshes on tab change too so the
  // count updates fast after Approve/Deny on the /approvals page.
  //
  // Slice F adds the WebSocket subscription below so the count updates
  // *instantly* on add/resolve. The 60-s poll remains as a backstop — if the
  // socket is down (network hiccup, DO restart) the badge still recovers on
  // the next tick.
  useEffect(() => {
    if (minimal || !client.isLoaded || !client.isSignedIn) return;
    let cancelled = false;
    const tick = () => {
      client
        .get<McpApproval[]>('/api/mcp/approvals?status=pending')
        .then((a) => { if (!cancelled) setPendingApprovals(a.length); })
        .catch(() => { /* swallow — keep last count */ });
    };
    tick();
    const id = setInterval(tick, 60_000);
    const onFocus = () => tick();
    window.addEventListener('focus', onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [client, minimal, pathname]);

  // Slice F — live WebSocket updates. add/resolve events bump the counter
  // immediately so the badge doesn't have to wait for the next 60-s poll.
  // Phase 4 Slice 1 — agent_run_pending bumps the agent-runs counter (the
  // server-side scheduler fires every minute and may enqueue runs while
  // the user has the tab focused but not on /agents).
  useRealtime((ev) => {
    if (ev.kind === 'approval_added') {
      setPendingApprovals((n) => n + 1);
    } else if (ev.kind === 'approval_resolved') {
      setPendingApprovals((n) => Math.max(0, n - 1));
    } else if (ev.kind === 'agent_run_pending') {
      setPendingAgentRuns((n) => n + 1);
    }
  }, [minimal]);

  const navLink = (label: string, to: string) => (
    <Link
      key={to}
      to={to}
      className={pathname === to || pathname.startsWith(to + '/') ? 'active' : ''}
    >
      {label}
    </Link>
  );

  if (minimal) {
    return (
      <header className="gw-header">
        <div className="container gw-header__inner">
          <Logo onClick={() => navigate('/sign-in')} />
          <div className="gw-header__spacer" />
          <div className="gw-header__tools">
            <button className="btn btn-ghost btn-icon" onClick={toggle} aria-label="Toggle theme" type="button">
              {theme === 'dark' ? <Icon.Sun /> : <Icon.Moon />}
            </button>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="gw-header">
      <div className="container gw-header__inner">
        <Logo onClick={() => navigate('/dashboard')} />
        <nav className="gw-header__nav">
          {navLink('Chat', '/dashboard')}
          {navLink('Systems', '/systems')}
          {navLink('Notebooks', '/notebooks')}
          <Link
            key="/agents"
            to="/agents"
            onClick={() => setPendingAgentRuns(0)}
            className={pathname === '/agents' || pathname.startsWith('/agents/') ? 'active' : ''}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            Agents
            {pendingAgentRuns > 0 && (
              <span
                className="pill pill-amber"
                style={{ fontSize: 10, padding: '1px 6px', lineHeight: '14px' }}
                aria-label={`${pendingAgentRuns} scheduled run${pendingAgentRuns === 1 ? '' : 's'} pending`}
              >
                {pendingAgentRuns}
              </span>
            )}
          </Link>
          {navLink('Prompts', '/prompts')}
          {navLink('Simulator', '/simulator')}
          {navLink('MCP', '/mcp')}
          {navLink('Cluster', '/cluster')}
          <Link
            key="/approvals"
            to="/approvals"
            className={pathname === '/approvals' || pathname.startsWith('/approvals/') ? 'active' : ''}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            Approvals
            {pendingApprovals > 0 && (
              <span
                className="pill pill-amber"
                style={{ fontSize: 10, padding: '1px 6px', lineHeight: '14px' }}
                aria-label={`${pendingApprovals} pending`}
              >
                {pendingApprovals}
              </span>
            )}
          </Link>
          {navLink('Feedback', '/feedback')}

          {/* Admin dropdown — only rendered for super-admins */}
          {isAdmin && (
            <div ref={adminRef} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={() => setAdminOpen((o) => !o)}
                aria-expanded={adminOpen}
                aria-haspopup="true"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 2px',
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  color: isAdminRoute ? 'var(--vps-blue-300)' : 'var(--gw-text)',
                  fontWeight: isAdminRoute ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  opacity: 0.9,
                }}
              >
                Admin
                <span style={{
                  fontSize: 9,
                  display: 'inline-block',
                  transform: adminOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                }}>
                  ▾
                </span>
              </button>

              {adminOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'var(--gw-glass-bg, rgba(20,22,35,0.98))',
                    border: '1px solid var(--gw-border)',
                    borderRadius: 10,
                    padding: '6px 4px',
                    minWidth: 130,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    zIndex: 200,
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                  }}
                >
                  {ADMIN_LINKS.map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      onClick={() => setAdminOpen(false)}
                      style={{
                        display: 'block',
                        padding: '7px 14px',
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: pathname === to ? 600 : 400,
                        color: pathname === to ? 'var(--vps-blue-300)' : 'var(--gw-text)',
                        textDecoration: 'none',
                        background: pathname === to ? 'rgba(114,164,242,0.1)' : 'transparent',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="gw-header__spacer" />
        <div className="gw-header__tools">
          <ConnectionBadge tiny mode="loopback" state="ok" />
          <button className="btn btn-ghost btn-icon" onClick={toggle} aria-label="Toggle theme" type="button">
            {theme === 'dark' ? <Icon.Sun /> : <Icon.Moon />}
          </button>
          <UserButton />
        </div>
      </div>
    </header>
  );
}
